import React from 'react'
import { CardFragment, DigitalCardOrder } from 'src/graphql/generated/graphql'
import { useAccount, useActions, useState } from 'src/hooks'
import PrepareAct from '../PrepareAct/PrepareAct'
import { ActCheckout } from '../ActCheckout/ActCheckout'
import { useDigitalCardOrderPay } from 'src/react_query/mutations/hooks'
import DialogHeader from './DialogHeader'
import { LEGACY_SITE_URL } from 'src/app/api'
import { ActRoute } from 'src/act/routes/ActRoute'
import { ActConfigRoute } from 'src/act/routes/ActConfiguration'
import { ActCheckoutRoute } from 'src/act/routes/ActCheckout'
import { useGetCard } from 'src/react_query'
import { ActSuccessRoute } from 'src/act/routes/ActSuccess'
import {
  LoadingSpinner,
  Text,
  Image,
  Flex,
} from '@sendoutcards/quantum-design-ui'
import { ActConfirmationScreen } from '../ActConfirmationScreen/ActConfirmationScreen'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import ExclusiveOfferScreen from 'src/onboarding/components/ExclusiveOfferScreen'

type ActSetupProps = {
  route: ActRoute
  cardId: string
}

const ActSetup: React.FC<ActSetupProps> = props => {
  const { route, cardId } = props
  const actions = useActions()
  const account = useAccount()
  const isSparse = !account.username

  const baseURL = LEGACY_SITE_URL

  const digitalCardOrderPayMutation = useDigitalCardOrderPay()
  const getCardQuery = useGetCard({ id: cardId }, { suspense: true })
  const card = getCardQuery.data?.card

  if (!card) {
    throw new Error('Card not found!')
  }

  const [isLoading, setIsLoading] = useState(false)
  const [digitalOrder, setDigitalOrder] = useState<
    DigitalCardOrder | undefined
  >(undefined)

  const digitalCardToken =
    card?.digitalCard?.recipients && card.digitalCard?.recipients.length > 0
      ? card.digitalCard.recipients[0].token
      : undefined

  const [shareToken, setShareToken] = useState<string | undefined>(
    digitalCardToken,
  )

  const [shouldShowJoin, setShouldShowJoin] = useState(false)

  const handlePreparationComplete = (digiCardOrder: DigitalCardOrder) => {
    setDigitalOrder(digiCardOrder)
    if (digiCardOrder.data.total === 0) {
      setIsLoading(true)
      actOrderPay()
    } else {
      actions.openActCheckout(cardId)
    }
  }

  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false)

  const actOrderPay = async () => {
    const digitalCardPk = card?.digitalCard?.pk
    if (digitalCardPk) {
      const digiCard = await digitalCardOrderPayMutation.mutateAsync({
        digitalCardPk: digitalCardPk,
      })
      setIsLoading(false)
      if (digiCard.recipients.length > 0 && digiCard.recipients[0].token) {
        setShareToken(digiCard.recipients[0].token)
        actions.openActSuccess(cardId)
      }
    }
  }

  const recipientName =
    card?.digitalCard &&
    card?.digitalCard.recipients &&
    card?.digitalCard.recipients.length > 0 &&
    card?.digitalCard.recipients[0].contactRequest
      ? card.digitalCard.recipients[0].contactRequest.firstName
      : 'their'

  const step = (path: string, card: CardFragment): React.ReactNode => {
    if (path === ActConfigRoute.path)
      return (
        <PrepareAct
          card={card}
          onBack={() => actions.openCard(card.id)}
          onPreparationComplete={handlePreparationComplete}
        />
      )
    else if (path === ActCheckoutRoute.path)
      return (
        <>
          {digitalOrder && (
            <Flex
              style={{
                padding: '20px',
                flexDirection: 'column',
                background: '#f8f5f699',
                height: '100dvh',
              }}
            >
              <DialogHeader
                onBack={() => {
                  actions.openActConfiguration(card.id)
                }}
              />
              <ActCheckout
                order={digitalOrder}
                paymentFormDisplay={{
                  shouldShowPaymentForm: showPaymentMethodForm,
                  setShouldShowPaymentMethodForm: setShowPaymentMethodForm,
                }}
                item={{
                  title: card?.detailedSendableCard?.title ?? 'Custom Card',
                  imageUrl: card?.frontPreviewUrl,
                  isHorizontal: card?.isHorizontal ?? false,
                  price: digitalOrder?.data.items[0].amount ?? 0,
                  discount:
                    digitalOrder?.data.items[0].discounts[0] ?? undefined,
                }}
                onChangeAddon={() => {
                  console.log('on change addon')
                }}
                onChangeCard={() => console.log('change card')}
                onSubmit={actOrderPay}
                isProcessingPayment={digitalCardOrderPayMutation.isLoading}
              />
            </Flex>
          )}
        </>
      )
    else if (path === ActSuccessRoute.path)
      return (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '100%',
          }}
        >
          <Flex width="100%" inset="x2" justifyContent="center">
            <ActConfirmationScreen
              recipientName={recipientName}
              token={shareToken}
              messageBody={card.digitalCard?.shareMessageBody}
              messageTitle={card.digitalCard?.shareMessageTitle}
              card={card}
              onFinish={() => {
                if (isSparse) {
                  setShouldShowJoin(true)
                } else {
                  actions.openAccount()
                }
              }}
              onCopyToClipboard={() => {
                navigator.clipboard.writeText(`${baseURL}/act/${shareToken}`)
                return true
              }}
              url={`${baseURL}/act/${shareToken}`}
              isSparse={isSparse}
            />
          </Flex>
        </div>
      )
    else return <></>
  }

  if (!route.subroute) {
    return null
  }

  return (
    <>
      {isLoading ? (
        <Dialog
          mobileBreakPointOverride={799}
          maxWidth="320px"
          mobileOpenHeight="0px"
          isOpen={true}
          onClose={() => {
            // what to do here
          }}
          outerWrapperStyle={{ display: 'flex', flexDirection: 'column' }}
          wrapperStyle={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: '1',
            overflowX: 'auto',
          }}
          shouldHideTitleBar={true}
          padding="20px"
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
            }}
          >
            <Image
              isActive
              image={{ url: card.frontPreviewUrl }}
              width={card.isHorizontal ? '210px' : '150px'}
              height={card.isHorizontal ? '150px' : '210px'}
            />
            <Text
              content="Generating Shareable Card"
              type="largeBody"
              color="primaryHeading"
              weight="semiBold"
            />
            <LoadingSpinner size="medium" />
          </div>
        </Dialog>
      ) : shouldShowJoin ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            width: '100%',
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <ExclusiveOfferScreen
            onFinishedJoin={() => actions.openAccount()}
            onClose={() => actions.openAccount()}
          />
        </div>
      ) : (
        step(route.subroute.path, card)
      )}
    </>
  )
}

export default ActSetup
